import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Button,
  Modal,
  Table,
  Select,
  Input,
  Tag,
  DatePicker,
  Checkbox,
  Card,
  Divider,
  Tooltip,
} from "antd";
import {
  LinkOutlined,
  FilterOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { getRequest, patchRequest } from "../../../axios";
import {
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
}

function parseInvoiceDateIso(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw?.format === "function") {
    return raw.format("YYYY-MM-DD");
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

/**
 * Same invoice–challan linking UI as AiDocIntakePdf (edit mode):
 * GET invoice-challan-map/document/, PATCH invoice-challan-map/update-document/
 */
const InvoiceLinkChallansModal = ({
  open,
  onClose,
  onSuccess,
  invoiceUuid,
  invoicePk,
  initialVendorName,
  invoiceNumber,
  invoiceDateRaw,
  allowEmptySelection,
}) => {
  const [supplierList, setSupplierList] = useState([]);
  const [linkInvoiceVendorId, setLinkInvoiceVendorId] = useState(null);
  const [linkInvoiceNumber, setLinkInvoiceNumber] = useState("");
  const [linkInvoiceDateIso, setLinkInvoiceDateIso] = useState(null);

  const [availableChallans, setAvailableChallans] = useState([]);
  const [selectedChallanIds, setSelectedChallanIds] = useState([]);
  const [loadingChallans, setLoadingChallans] = useState(false);
  const [loadingLinkedChallans, setLoadingLinkedChallans] = useState(false);
  const [challanSearchText, setChallanSearchText] = useState("");
  const [challanDateRange, setChallanDateRange] = useState(null);
  const [debouncedChallanSearchText, setDebouncedChallanSearchText] =
    useState("");
  const [linkingChallans, setLinkingChallans] = useState(false);
  const [hasInitializedChallansFromEditApi, setHasInitializedChallansFromEditApi] =
    useState(false);

  const resetLocalState = useCallback(() => {
    setLinkInvoiceVendorId(null);
    setLinkInvoiceNumber("");
    setLinkInvoiceDateIso(null);
    setAvailableChallans([]);
    setSelectedChallanIds([]);
    setChallanSearchText("");
    setChallanDateRange(null);
    setDebouncedChallanSearchText("");
    setHasInitializedChallansFromEditApi(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      setDebouncedChallanSearchText((challanSearchText || "").trim());
    }, 600);
    return () => clearTimeout(id);
  }, [challanSearchText, open]);

  useEffect(() => {
    if (!open) return;
    const fetchVendors = async () => {
      try {
        const res = await getRequest("vendors/", { page: 1, page_size: 500 });
        const payload = res?.data;
        const raw =
          payload?.results?.data ??
          payload?.results ??
          payload?.data ??
          payload;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
            ? raw.results
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        setSupplierList(list);
      } catch (err) {
        console.error("Failed to fetch vendors:", err);
        ShowErrorMsg("Failed to load vendors");
      }
    };
    fetchVendors();
  }, [open]);

  const fetchChallans = async ({
    reset = false,
    vendorOverride,
    searchOverride,
  } = {}) => {
    if (
      !hasInitializedChallansFromEditApi &&
      vendorOverride == null
    ) {
      return;
    }
    const vendorToUse = vendorOverride ?? linkInvoiceVendorId;
    if (!vendorToUse || !open) {
      setAvailableChallans([]);
      return;
    }

    setLoadingChallans(true);
    try {
      const dateRange =
        reset || !challanDateRange || challanDateRange.length !== 2
          ? null
          : challanDateRange;
      const start =
        dateRange && dateRange[0]
          ? typeof dateRange[0]?.format === "function"
            ? dateRange[0].format("YYYY-MM-DD")
            : String(dateRange[0]).slice(0, 10)
          : null;
      const end =
        dateRange && dateRange[1]
          ? typeof dateRange[1]?.format === "function"
            ? dateRange[1].format("YYYY-MM-DD")
            : String(dateRange[1]).slice(0, 10)
          : null;

      const search =
        searchOverride !== undefined
          ? String(searchOverride).trim()
          : reset
            ? ""
            : (debouncedChallanSearchText || "").trim();

      const params = {
        vendor: vendorToUse,
        not_linked_to_invoice: true,
        ...(start ? { start_date: start } : {}),
        ...(end ? { end_date: end } : {}),
        ...(search ? { search } : {}),
      };

      const res = await getRequest("challans/", params);
      const payload = res?.data;
      const challans =
        payload?.results?.data || payload?.results || payload?.data || [];
      setAvailableChallans(Array.isArray(challans) ? challans : []);
    } catch (err) {
      console.error("Failed to fetch challans:", err);
      ShowErrorMsg("Failed to load challans");
      setAvailableChallans([]);
    } finally {
      setLoadingChallans(false);
    }
  };

  const loadLinkedDocument = useCallback(async () => {
    const uuid = invoiceUuid ?? null;
    const pk = invoicePk != null ? Number(invoicePk) : null;
    if (!uuid && !(Number.isFinite(pk) && pk > 0)) {
      ShowErrorMsg("Invoice reference is missing.");
      return;
    }

    try {
      setLoadingLinkedChallans(true);
      const res = uuid
        ? await getRequest("invoice-challan-map/document/", {
            invoice_id: uuid,
          })
        : await getRequest(`invoice-challan-map/document/${pk}/`);
      const payload = res?.data;
      const results = payload?.data ?? payload?.results ?? payload;
      const vendorName = results?.vendor ?? null;
      const invDate =
        results?.invoice_date ??
        results?.date ??
        results?.document_date ??
        null;
      const challansArray = Array.isArray(results?.challans)
        ? results.challans
        : Array.isArray(results?.data)
          ? results.data
          : [];

      const normalizedChallans = challansArray.map((c) => ({
        ...c,
        id: c?.id ?? c?.challan_id ?? c?.challanId,
      }));

      const checkedChallansFromApi = normalizedChallans.filter(
        (c) =>
          c?.is_checked === true ||
          c?.is_checked === "true" ||
          c?.is_checked === 1 ||
          c?.is_checked === "1" ||
          c?.is_selected === true ||
          c?.is_selected === "true" ||
          c?.is_selected === 1 ||
          c?.is_selected === "1",
      );

      const challanIds = checkedChallansFromApi
        .map((c) => c?.id)
        .filter(Boolean);

      if (vendorName) setLinkInvoiceVendorId(String(vendorName));
      else if (initialVendorName)
        setLinkInvoiceVendorId(String(initialVendorName));

      if (invDate) {
        const s = String(invDate).slice(0, 10);
        setLinkInvoiceDateIso(
          /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : parseInvoiceDateIso(invDate),
        );
      } else {
        const fromProp = parseInvoiceDateIso(invoiceDateRaw);
        setLinkInvoiceDateIso(fromProp);
      }

      if (invoiceNumber) setLinkInvoiceNumber(String(invoiceNumber));
      else if (results?.invoice_number)
        setLinkInvoiceNumber(String(results.invoice_number));

      setAvailableChallans(normalizedChallans);
      setSelectedChallanIds(challanIds);
      setHasInitializedChallansFromEditApi(true);
    } catch (err) {
      ShowErrorMsg(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to load linked challans",
      );
      setHasInitializedChallansFromEditApi(true);
    } finally {
      setLoadingLinkedChallans(false);
    }
  }, [
    invoiceUuid,
    invoicePk,
    initialVendorName,
    invoiceDateRaw,
    invoiceNumber,
  ]);

  useEffect(() => {
    if (!open) {
      resetLocalState();
      return;
    }
    loadLinkedDocument();
  }, [open, loadLinkedDocument, resetLocalState]);

  const handleClose = () => {
    resetLocalState();
    onClose();
  };

  const handleVendorChange = (vendorId) => {
    setLinkInvoiceVendorId(vendorId);
    setSelectedChallanIds([]);
    fetchChallans({ vendorOverride: vendorId });
  };

  const handleChallanSelection = (checked, challanId) => {
    if (checked) {
      setSelectedChallanIds((prev) =>
        prev.includes(challanId) ? prev : [...prev, challanId],
      );
    } else {
      setSelectedChallanIds((prev) => prev.filter((id) => id !== challanId));
    }
  };

  const handleSelectAllChallans = (checked) => {
    if (checked) {
      const filtered = getFilteredChallans();
      setSelectedChallanIds(filtered.map((c) => c.id));
    } else {
      setSelectedChallanIds([]);
    }
  };

  const handleApplyChallanFilters = () => {
    const next = (challanSearchText || "").trim();
    setDebouncedChallanSearchText(next);
    fetchChallans({ searchOverride: next });
  };

  const handleResetChallanFilters = () => {
    setChallanSearchText("");
    setChallanDateRange(null);
    fetchChallans({ reset: true });
  };

  const getFilteredChallans = () => {
    let filtered = [...availableChallans];
    if (challanSearchText) {
      const searchLower = challanSearchText.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          String(c.challan_number || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(c.gr_id || "")
            .toLowerCase()
            .includes(searchLower),
      );
    }
    if (challanDateRange && challanDateRange.length === 2) {
      const [startDate, endDate] = challanDateRange;
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter((c) => {
        const challanDate = c.date || c.challan_date || c.created_at;
        if (!challanDate) return false;
        const date = new Date(challanDate);
        return date >= start && date <= end;
      });
    }
    return filtered;
  };

  const handleUpdateLinkedChallans = async () => {
    if (!allowEmptySelection && selectedChallanIds.length === 0) {
      ShowErrorMsg("Please select at least one challan.");
      return;
    }

    const vendorForMap = (linkInvoiceVendorId && String(linkInvoiceVendorId).trim()) || "";
    if (!vendorForMap) {
      ShowErrorMsg("Please select a vendor.");
      return;
    }

    const invoiceDateIso =
      linkInvoiceDateIso || parseInvoiceDateIso(invoiceDateRaw);
    if (!invoiceDateIso) {
      ShowErrorMsg(
        "Invoice date is required. Ensure the invoice has a valid date.",
      );
      return;
    }

    const uuid = invoiceUuid ?? null;
    const pk = invoicePk != null ? Number(invoicePk) : null;
    const invoiceIdForApi = uuid || (Number.isFinite(pk) ? pk : null);
    if (invoiceIdForApi == null || invoiceIdForApi === "") {
      ShowErrorMsg("Invoice ID not found.");
      return;
    }

    const n = selectedChallanIds.length;
    const confirmed = await new Promise((resolve) => {
      Modal.confirm({
        title: n === 0 ? "Remove all challan links?" : "Update linked challans",
        content:
          n === 0
            ? "No challans will remain linked to this invoice."
            : `Apply mapping with ${n} selected challan(s).`,
        okText: n === 0 ? "Yes, unlink all" : "Yes, update",
        cancelText: "Cancel",
        okButtonProps: { style: { borderRadius: 8 } },
        cancelButtonProps: { style: { borderRadius: 8 } },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    const challanIdsNumeric = selectedChallanIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));

    try {
      setLinkingChallans(true);
      await patchRequest(`invoice-challan-map/update-document/`, {
        invoice_id: invoiceIdForApi,
        vendor: vendorForMap,
        invoice_date: invoiceDateIso,
        selected_challan_ids: challanIdsNumeric,
      });
      SuccessNotificationMsg("Invoice–challan mapping updated");
      handleClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      ShowErrorMsg(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to update challan mapping",
      );
    } finally {
      setLinkingChallans(false);
    }
  };

  const filtered = getFilteredChallans();
  const primaryDisabled =
    linkingChallans ||
    !linkInvoiceVendorId ||
    (!allowEmptySelection && selectedChallanIds.length === 0);

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "#094E8F",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LinkOutlined style={{ color: "#fff", fontSize: 16 }} />
          </div>
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontWeight: 800 }}>Link Challans to Invoice</div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
              Unlink by clearing checkboxes, or add challans after changing
              vendor
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      wrapClassName="link-challans-modal"
      width="60%"
      style={{ top: 24 }}
      bodyStyle={{ padding: 0 }}
      footer={[
        <Button
          key="cancel"
          type="primary"
          className="CancelGradiantBtn"
          onClick={handleClose}
        >
          Cancel
        </Button>,
        <Button
          key="update"
          type="primary"
          className="SaveGradiantBtn"
          onClick={handleUpdateLinkedChallans}
          loading={linkingChallans}
          disabled={primaryDisabled}
          icon={<LinkOutlined />}
        >
          {`Update Linked Challans (${selectedChallanIds.length})`}
        </Button>,
      ]}
      destroyOnClose
    >
      <div style={{ padding: 16, maxHeight: "78vh", overflowY: "auto" }}>
        {loadingLinkedChallans ? (
          <div style={{ padding: 40, textAlign: "center", color: "#8c8c8c" }}>
            Loading linked challans…
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={16}>
                <Card
                  title={
                    <>
                      Vendor <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  size="small"
                  style={{ marginBottom: 0 }}
                  bodyStyle={{ padding: 12 }}
                >
                  <Row gutter={[16, 12]}>
                    <Col xs={24} sm={12}>
                      <Select
                        placeholder="Select vendor"
                        style={{ width: "100%" }}
                        value={linkInvoiceVendorId}
                        onChange={handleVendorChange}
                        showSearch
                        optionFilterProp="label"
                        options={supplierList.map((v) => ({
                          label: v.name || "—",
                          value: v.name || "",
                        }))}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card
                  title="Selection Summary"
                  size="small"
                  style={{ marginBottom: 0, backgroundColor: "#f0f9ff" }}
                  bodyStyle={{ padding: 12 }}
                >
                  <Row gutter={[12, 10]}>
                    <Col span={24}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          paddingBottom: 9,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>
                          Selected challans
                        </span>
                        <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                          {selectedChallanIds.length}
                        </Tag>
                      </div>
                    </Col>
                  </Row>
                  {linkInvoiceNumber ? (
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                      Invoice: <strong>{linkInvoiceNumber}</strong>
                    </div>
                  ) : null}
                </Card>
              </Col>
            </Row>

            {linkInvoiceVendorId && (
              <>
                <Card
                  title="Available Challans"
                  size="small"
                  style={{ marginBottom: 0 }}
                  bodyStyle={{ padding: 0 }}
                  extra={
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 8,
                        justifyContent: "flex-end",
                        maxWidth: 820,
                      }}
                    >
                      <div
                        style={{ width: 260, minWidth: 200, flex: "1 1 220px" }}
                      >
                        <Input
                          placeholder="Search challan no."
                          value={challanSearchText}
                          onChange={(e) => setChallanSearchText(e.target.value)}
                          allowClear
                        />
                      </div>
                      <div
                        style={{ width: 260, minWidth: 220, flex: "1 1 260px" }}
                      >
                        <DatePicker.RangePicker
                          value={challanDateRange}
                          onChange={setChallanDateRange}
                          format="DD-MM-YYYY"
                          placeholder={["Start", "End"]}
                          style={{ width: "100%" }}
                        />
                      </div>

                      <Button
                        type="primary"
                        icon={
                          <Tooltip title="Apply filters">
                            <FilterOutlined style={{ fontSize: "20px" }} />
                          </Tooltip>
                        }
                        onClick={handleApplyChallanFilters}
                        disabled={loadingChallans}
                        style={{
                          backgroundColor: "#094e8f",
                          color: "#ffffff",
                          border: "none",
                          width: "40px",
                          height: "40px",
                          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                          borderRadius: "4px",
                        }}
                      />

                      <Button
                        type="primary"
                        icon={
                          <Tooltip title="Reset filters">
                            <UndoOutlined style={{ fontSize: "20px" }} />
                          </Tooltip>
                        }
                        onClick={handleResetChallanFilters}
                        disabled={loadingChallans}
                        style={{
                          backgroundColor: "#094e8f",
                          color: "#ffffff",
                          border: "none",
                          width: "40px",
                          height: "40px",
                          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  }
                >
                  <Table
                    rowKey="id"
                    dataSource={filtered}
                    loading={loadingChallans}
                    tableLayout="fixed"
                    className="link-challans-table"
                    columns={[
                      {
                        title: (
                          <Checkbox
                            checked={
                              filtered.length > 0 &&
                              filtered.every((c) =>
                                selectedChallanIds.includes(c.id),
                              )
                            }
                            indeterminate={
                              selectedChallanIds.length > 0 &&
                              selectedChallanIds.length < filtered.length
                            }
                            onChange={(e) =>
                              handleSelectAllChallans(e.target.checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        ),
                        key: "checkbox",
                        width: 50,
                        render: (_, record) => (
                          <Checkbox
                            checked={selectedChallanIds.includes(record.id)}
                            onChange={(e) =>
                              handleChallanSelection(
                                e.target.checked,
                                record.id,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        ),
                      },
                      {
                        title: "Challan Number",
                        dataIndex: "challan_number",
                        key: "challan_number",
                        width: 150,
                        render: (val) => val || "—",
                      },
                      {
                        title: "Date",
                        key: "date",
                        width: 120,
                        render: (_, record) =>
                          formatDate(
                            record.challan_date ||
                              record.date ||
                              record.created_at,
                          ),
                      },
                      {
                        title: "Status",
                        key: "status",
                        width: 160,
                        ellipsis: true,
                        render: (_, record) => {
                          const text =
                            record.status_display || record.status || "—";
                          return (
                            <Tooltip title={text} placement="topLeft">
                              <Tag
                                color={
                                  record.status === "Draft" ? "orange" : "blue"
                                }
                                style={{
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                  margin: 0,
                                }}
                              >
                                {text}
                              </Tag>
                            </Tooltip>
                          );
                        },
                      },
                      {
                        title: "Items",
                        key: "total_items",
                        width: 80,
                        align: "right",
                        render: (_, record) =>
                          record.total_items != null
                            ? String(record.total_items)
                            : "—",
                      },
                      {
                        title: "Transport",
                        key: "transport",
                        width: 110,
                        render: (_, record) =>
                          record.transport_type_display ||
                          record.transport_type ||
                          "—",
                      },
                      {
                        title: "Created By",
                        key: "created_by_name",
                        width: 120,
                        render: (_, record) => record.created_by_name || "—",
                      },
                      {
                        title: "Document",
                        key: "document",
                        width: 110,
                        render: (_, record) =>
                          record.document_file_url ? (
                            <a
                              href={record.document_file_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View
                            </a>
                          ) : (
                            "—"
                          ),
                      },
                    ]}
                    pagination={false}
                    scroll={{ x: 1020, y: 320 }}
                    size="small"
                    bordered
                    expandable={{
                      expandedRowRender: (record) => {
                        const supplier =
                          record.supplier_address ||
                          record.supplier_gstin ||
                          record.supplier_phone ||
                          record.supplier_email;
                        const delivery =
                          record.delivery_address ||
                          record.delivery_contact_person ||
                          record.delivery_contact_phone;
                        const transport =
                          record.vehicle_number ||
                          record.driver_name ||
                          record.driver_phone ||
                          record.transport_company ||
                          record.lr_number;
                        const items = Array.isArray(record.challan_items)
                          ? record.challan_items
                          : [];

                        return (
                          <div style={{ padding: "8px 8px 0" }}>
                            <Row gutter={[16, 8]}>
                              <Col xs={24} md={8}>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: 6,
                                  }}
                                >
                                  Supplier
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {supplier ? (
                                    <>
                                      {record.supplier_gstin ? (
                                        <div>
                                          GSTIN: {record.supplier_gstin}
                                        </div>
                                      ) : null}
                                      {record.supplier_address ? (
                                        <div>{record.supplier_address}</div>
                                      ) : null}
                                      {record.supplier_phone ? (
                                        <div>
                                          Phone: {record.supplier_phone}
                                        </div>
                                      ) : null}
                                      {record.supplier_email ? (
                                        <div>
                                          Email: {record.supplier_email}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </Col>

                              <Col xs={24} md={8}>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: 6,
                                  }}
                                >
                                  Delivery
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {delivery ? (
                                    <>
                                      {record.delivery_address ? (
                                        <div>{record.delivery_address}</div>
                                      ) : null}
                                      {record.delivery_contact_person ? (
                                        <div>
                                          Contact:{" "}
                                          {record.delivery_contact_person}
                                        </div>
                                      ) : null}
                                      {record.delivery_contact_phone ? (
                                        <div>
                                          Phone:{" "}
                                          {record.delivery_contact_phone}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </Col>

                              <Col xs={24} md={8}>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: 6,
                                  }}
                                >
                                  Transport
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {transport ? (
                                    <>
                                      {record.transport_company ? (
                                        <div>{record.transport_company}</div>
                                      ) : null}
                                      {record.vehicle_number ? (
                                        <div>
                                          Vehicle: {record.vehicle_number}
                                        </div>
                                      ) : null}
                                      {record.driver_name ? (
                                        <div>Driver: {record.driver_name}</div>
                                      ) : null}
                                      {record.driver_phone ? (
                                        <div>Phone: {record.driver_phone}</div>
                                      ) : null}
                                      {record.lr_number ? (
                                        <div>LR: {record.lr_number}</div>
                                      ) : null}
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </Col>
                            </Row>

                            {record.remarks ? (
                              <>
                                <Divider style={{ margin: "12px 0" }} />
                                <div
                                  style={{ fontWeight: 600, marginBottom: 6 }}
                                >
                                  Remarks
                                </div>
                                <div style={{ color: "#595959" }}>
                                  {record.remarks}
                                </div>
                              </>
                            ) : null}

                            {items.length > 0 ? (
                              <>
                                <Divider style={{ margin: "12px 0" }} />
                                <div
                                  style={{
                                    fontWeight: 600,
                                    marginBottom: 8,
                                  }}
                                >
                                  Challan Items ({items.length})
                                </div>
                                <Table
                                  rowKey="id"
                                  size="small"
                                  dataSource={items}
                                  pagination={false}
                                  columns={[
                                    {
                                      title: "Item",
                                      key: "item_name",
                                      render: (_, it) =>
                                        it.item_name ||
                                        it.item_description ||
                                        "—",
                                    },
                                    {
                                      title: "Qty.",
                                      dataIndex: "quantity",
                                      key: "quantity",
                                      width: 90,
                                      align: "right",
                                      render: (v) =>
                                        v != null && v !== "" ? v : "—",
                                    },
                                    {
                                      title: "UoM.",
                                      dataIndex: "uom",
                                      key: "uom",
                                      width: 80,
                                      render: (v) => v || "—",
                                    },
                                  ]}
                                />
                              </>
                            ) : null}
                          </div>
                        );
                      },
                      rowExpandable: (record) =>
                        !!record.remarks ||
                        !!record.document_file_url ||
                        !!record.supplier_address ||
                        !!record.delivery_address ||
                        (Array.isArray(record.challan_items) &&
                          record.challan_items.length > 0),
                    }}
                    locale={{
                      emptyText: (
                        <div
                          style={{
                            padding: "24px",
                            textAlign: "center",
                            color: "#8c8c8c",
                          }}
                        >
                          {loadingChallans
                            ? "Loading challans…"
                            : "No challans available for this vendor"}
                        </div>
                      ),
                    }}
                    onRow={(record) => ({
                      onClick: () => {
                        const isSelected = selectedChallanIds.includes(
                          record.id,
                        );
                        handleChallanSelection(!isSelected, record.id);
                      },
                      style: {
                        cursor: "pointer",
                        backgroundColor: selectedChallanIds.includes(record.id)
                          ? "#e6f7ff"
                          : "transparent",
                      },
                    })}
                  />
                </Card>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

InvoiceLinkChallansModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  invoiceUuid: PropTypes.string,
  invoicePk: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialVendorName: PropTypes.string,
  invoiceNumber: PropTypes.string,
  invoiceDateRaw: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.number,
  ]),
  allowEmptySelection: PropTypes.bool,
};

InvoiceLinkChallansModal.defaultProps = {
  onSuccess: undefined,
  invoiceUuid: null,
  invoicePk: null,
  initialVendorName: "",
  invoiceNumber: "",
  invoiceDateRaw: null,
  allowEmptySelection: true,
};

export default InvoiceLinkChallansModal;
